/**
 * SessionReconnector — per-session exponential backoff reconnection.
 *
 * Reusable across multiple sessions in the multitasking WebSocket pool.
 * Unit-tested independently from React / WS transport.
 */

import type { ReconnectInfo } from '../types/session.types';

export interface SessionReconnectorOptions {
  /** Maximum number of connection attempts before giving up */
  maxAttempts: number;
  /**
   * Base delay in milliseconds. Actual delay for attempt N is
   * `baseDelay * 2^(N-1)` ms, capped to a reasonable ceiling by callers.
   */
  baseDelay: number;
  /**
   * Called after each attempt (including the first) with current progress,
   * and with `null` when reconnection is no longer active (success or abort).
   */
  onStatusChange: (info: ReconnectInfo | null) => void;
  /** Called when all attempts are exhausted without a successful connection */
  onFailed: () => void;
  /**
   * The actual connection function. Should resolve `true` if the connection
   * was established successfully, `false` otherwise.
   */
  connect: () => Promise<boolean>;
}

/**
 * Manages reconnection for a single WebSocket session with exponential
 * backoff and a visible countdown between attempts.
 *
 * Guards:
 * - Only one reconnection loop can run at a time (`attempt()` returns
 *   `false` immediately if a loop is already running).
 * - Once all attempts are exhausted the instance is "exhausted" and
 *   `attempt()` returns `false` until `resetExhausted()` is called.
 */
export class SessionReconnector {
  private readonly options: SessionReconnectorOptions;

  /** True while a reconnection loop is running */
  private reconnecting = false;
  /** True once all attempts have failed — blocks future `attempt()` calls */
  private exhausted = false;
  /** Abort flag — checked during countdown ticks */
  private aborted = false;

  constructor(options: SessionReconnectorOptions) {
    this.options = options;
  }

  /**
   * Start the reconnection loop. Returns `true` if a connection was
   * established within `maxAttempts` tries, `false` otherwise.
   *
   * - Returns `false` immediately if a loop is already running.
   * - Returns `false` immediately if the instance is exhausted
   *   (call `resetExhausted()` first).
   */
  async attempt(): Promise<boolean> {
    if (this.reconnecting || this.exhausted) return false;

    this.reconnecting = true;
    this.aborted = false;

    const { maxAttempts, baseDelay, onStatusChange, onFailed, connect } = this.options;

    for (let i = 1; i <= maxAttempts; i++) {
      if (this.aborted) {
        onStatusChange(null);
        this.reconnecting = false;
        return false;
      }

      onStatusChange({ attempt: i, maxAttempts, countdown: 0 });

      const success = await connect();

      if (success) {
        onStatusChange(null);
        this.reconnecting = false;
        return true;
      }

      // Wait with visible countdown before the next attempt (skip after last)
      if (i < maxAttempts) {
        const delayMs = baseDelay * Math.pow(2, i - 1);
        const delaySec = Math.max(1, Math.round(delayMs / 1000));

        for (let sec = delaySec; sec > 0; sec--) {
          if (this.aborted) {
            onStatusChange(null);
            this.reconnecting = false;
            return false;
          }
          onStatusChange({ attempt: i, maxAttempts, countdown: sec });
          await sleep(1000);
        }
      }
    }

    // All attempts exhausted
    this.exhausted = true;
    this.reconnecting = false;
    onStatusChange(null);
    onFailed();
    return false;
  }

  /**
   * Abort an in-progress reconnection loop at the next countdown tick.
   * Has no effect if no loop is running.
   */
  abort(): void {
    this.aborted = true;
  }

  /**
   * Allow `attempt()` to run again after exhaustion.
   * Intended for a manual "retry" button shown to the user after failure.
   */
  resetExhausted(): void {
    this.exhausted = false;
  }
}

/** Small helper to keep the loop body readable */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
